import { localizePath as __lp } from "@/i18n/locales";
import { useEffect, useMemo } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ScrollReveal } from "@/components/ScrollReveal";
import ScreenshotLightbox from "@/components/ScreenshotLightbox";
import InlineLeadForm from "@/components/InlineLeadForm";

import { useReference } from "@/hooks/usePublicContent";
import { referenceIcon } from "@/lib/referenceIcons";
import { ArrowRight, Sparkles, Check, Loader2 } from "lucide-react";
import { scrollToSection } from "@/lib/scrollToSection";
import { assetImage } from "@/lib/media";
import { trackConversion } from "@/lib/tracking";
import { trackAnalyticsConversion } from "@/lib/analytics";
import { useLocale } from "@/i18n/LocaleProvider";
import { useTranslation } from "react-i18next";
import { LANG_HREFLANG } from "@/i18n/locales";

/** Split a [[bracketed]] phrase out of a headline so it can be gradient-styled. */
const renderTitle = (title: string) => {
  const parts = title.split(/\[\[(.+?)\]\]/);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className="gradient-text">
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
};

const SITE = "https://devcore.systems";

const CaseStudy = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { lang, localize } = useLocale();
  const { t } = useTranslation();
  const { data: reference, isLoading } = useReference(slug, lang);
  const study = reference?.content;

  useEffect(() => {
    if (reference) {
      trackConversion("case_study_view", { content_name: reference.slug });
      trackAnalyticsConversion("case_study_view", reference.slug);
    }
  }, [reference]);

  const goToBlueprint = () => {
    if (!scrollToSection("blueprint")) navigate(localize("/#blueprint"));
  };

  const jsonLd = useMemo(() => {
    if (!reference || !study) return undefined;
    const langPath = lang === "en" ? "" : `/${lang}`;
    const canonical = `${SITE}${__lp(`/references/${reference.slug}`, lang)}`;
    const logo = `${SITE}/favicon.png`;
    const plainTitle = study.headline.replace(/\[\[(.+?)\]\]/g, "$1").trim();

    const organization = {
      "@type": "Organization",
      "@id": `${SITE}/#organization`,
      name: "DevCore",
      url: `${SITE}/`,
      logo: { "@type": "ImageObject", url: logo },
      sameAs: [
        "https://www.devcore.systems",
        "https://www.linkedin.com/company/devcore-websystems",
        "https://datora.de",
      ],
      description:
        "DevCore is a custom software studio building bespoke SaaS, portals, web apps, CRMs and AI tools.",
    };

    return [
      organization,
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${canonical}#article`,
        headline: plainTitle,
        description: study.metaDescription,
        url: canonical,
        inLanguage: LANG_HREFLANG[lang],
        image: study.screenshots.map((s) => s.src),
        author: { "@id": `${SITE}/#organization` },
        publisher: { "@id": `${SITE}/#organization` },
        about: study.client,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${canonical}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: t("caseStudy.home"), item: `${SITE}${langPath}/` },
          { "@type": "ListItem", position: 2, name: t("caseStudy.references"), item: `${SITE}${__lp("/references", lang)}` },
          { "@type": "ListItem", position: 3, name: plainTitle, item: canonical },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ImageGallery",
        "@id": `${canonical}#gallery`,
        name: study.galleryTitle,
        image: study.screenshots.map((s) => ({
          "@type": "ImageObject",
          url: s.src,
          caption: s.alt,
        })),
      },
    ];
  }, [reference, study, lang, t]);

  // While the query is in flight we render a lightweight shell so the router
  // never flashes a 404 before the data lands (and prerender can wait for it).
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="animate-spin text-accent" />
        </div>
      </div>
    );
  }

  if (!reference || !study || !reference.has_case_study) {
    return <Navigate to={localize("/404")} replace />;
  }

  const langPath = lang === "en" ? "" : `/${lang}`;
  const canonical = `${SITE}${__lp(`/references/${reference.slug}`, lang)}`;
  const ogImage = study.screenshots[0]?.src ?? "https://devcore.systems/og-image.png";

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={study.metaTitle}
        description={study.metaDescription}
        canonical={canonical}
        ogType="article"
        ogImage={ogImage}
        jsonLd={jsonLd}
      />
      <Navbar />

      <main>
      {/* ===== HERO ===== */}
      <header className="relative overflow-hidden pt-24 sm:pt-32 pb-14 sm:pb-20 md:pt-44 md:pb-28">
        <div className="absolute inset-0 tech-grid opacity-[0.12] pointer-events-none" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="aurora-blob aurora-1 left-[-10%] top-[-10%] w-[55vw] h-[55vw]" />
          <div className="aurora-blob aurora-2 right-[-15%] top-[10%] w-[50vw] h-[50vw]" />
        </div>
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-transparent to-background" />

        {/* Hero showcase: first gallery screenshot as a full-bleed grayscale backdrop, tilted back & fading toward the bottom-left */}
        {study.screenshots[0] && (
          <div
            aria-hidden
            className="hidden lg:block absolute inset-0 z-0 pointer-events-none overflow-hidden"
            style={{ perspective: "1600px" }}
          >
            <img
              src={assetImage(study.screenshots[0].src, 1400, { quality: 70 }).src}
              alt=""
              loading="eager"
              className="w-full h-full object-cover object-top opacity-[0.22]"
              style={{
                filter: "grayscale(1) contrast(1.05)",
                transform: "rotateY(-13deg) rotateX(6deg) rotateZ(-2deg) scale(1.35)",
                transformOrigin: "top right",
                WebkitMaskImage:
                  "linear-gradient(to bottom left, hsl(0 0% 0% / 1) 25%, hsl(0 0% 0% / 0) 82%)",
                maskImage:
                  "linear-gradient(to bottom left, hsl(0 0% 0% / 1) 25%, hsl(0 0% 0% / 0) 82%)",
              }}
            />
          </div>
        )}



        <div className="container relative z-10">
          <ScrollReveal className="max-w-4xl">
            <div className="flex items-end gap-4 mb-8">
              {reference.logo_url && (
                <img
                  {...assetImage(reference.logo_url, 260, { quality: 82, jpeg: false })}
                  alt={`${study.client} logo`}
                  className={`h-14 md:h-16 w-auto ${reference.logo_invert ? "brightness-0 invert" : ""}`}
                  loading="eager"
                />
              )}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border bg-card/60 backdrop-blur-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
                <span className="mono-label text-muted-foreground">{study.eyebrow}</span>
              </div>
            </div>

            <h1 className="text-[1.9rem] sm:text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-foreground">
              {renderTitle(study.headline)}
            </h1>
            <p className="mt-7 text-[15px] sm:text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              {study.subtitle}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
                <Link
                  to={localize("/contact")}
                  className="inline-flex w-full sm:w-auto justify-center items-center gap-2 px-8 py-4 btn-clip bg-primary text-primary-foreground btn-label-contrast font-semibold text-lg transition-transform duration-200 active:scale-[0.97] group"
                >
                  {t("caseStudy.ctaPrimary")}
                  <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </span>
              <button
                onClick={goToBlueprint}
                className="inline-flex w-full sm:w-auto justify-center items-center gap-2 px-8 py-4 btn-clip border border-border bg-card/60 backdrop-blur-sm text-foreground font-semibold text-lg transition-colors duration-200 hover:border-accent/50"
              >
                <Sparkles size={18} className="text-accent" />
                {t("caseStudy.ctaSecondary")}
              </button>
            </div>
          </ScrollReveal>

          {/* Facts + stats */}
          {study.facts.length > 0 && (
            <ScrollReveal delay={120} className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4">
              {study.facts.map((f) => (
                <div key={f.label} className="rounded-2xl border border-border bg-card/60 backdrop-blur-sm p-5">
                  <p className="font-mono text-[10px] tracking-widest text-accent uppercase mb-2">{f.label}</p>
                  <p className="text-foreground font-semibold leading-snug">{f.value}</p>
                </div>
              ))}
            </ScrollReveal>
          )}
        </div>
      </header>

      {/* ===== CHALLENGE / SOLUTION ===== */}
      <section className="py-14 sm:py-20 md:py-28 bg-secondary/30 relative overflow-hidden">
        <div className="container relative grid lg:grid-cols-2 gap-8 md:gap-10">
          <ScrollReveal direction="left" distance={60}>
            <div className="h-full rounded-3xl border border-border bg-card p-5 md:p-10">
              <p className="mono-label mb-3">{study.challengeKicker}</p>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground leading-tight mb-5">
                {study.challengeTitle}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">{study.challengeBody}</p>
              <ul className="space-y-3">
                {study.challengePoints.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-muted-foreground">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-destructive/70 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="right" distance={60} delay={100}>
            <div className="h-full rounded-3xl border border-accent/30 bg-card p-5 md:p-10 relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-20 pointer-events-none"
                style={{ background: "radial-gradient(circle at top right, hsl(var(--primary)) 0%, transparent 60%)" }}
              />
              <div className="relative z-10">
                <p className="mono-label mb-3">{study.solutionKicker}</p>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground leading-tight mb-5">
                  {study.solutionTitle}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-8">{study.solutionBody}</p>
                {study.stats.length > 0 && (
                  <div className="grid grid-cols-3 gap-4">
                    {study.stats.map((s) => {
                      // Render a leading non-digit symbol (e.g. ">", "Ø") smaller than the number
                      const m = s.value.match(/^([^\d]+?)\s*(\d.*)$/);
                      return (
                        <div key={s.label}>
                          <p className="text-2xl md:text-3xl font-bold gradient-text">
                            {m ? (
                              <>
                                <span className="text-base md:text-lg align-baseline mr-0.5">{m[1]}</span>
                                {m[2]}
                              </>
                            ) : (
                              s.value
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1 leading-snug text-balance max-w-[14ch]">{s.label}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ===== CAPABILITIES ===== */}
      {study.capabilities.length > 0 && (
        <section className="py-14 sm:py-20 md:py-28 relative overflow-hidden">
          <div className="absolute inset-0 tech-dots opacity-[0.15] pointer-events-none" />
          <div className="container relative">
            <ScrollReveal className="max-w-2xl mb-14">
              <p className="mono-label mb-3">{study.capabilitiesKicker}</p>
              <h2 className="text-[1.55rem] sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
                {study.capabilitiesTitle}
              </h2>
            </ScrollReveal>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {study.capabilities.map((c, i) => {
                const Icon = referenceIcon(c.icon);
                return (
                  <ScrollReveal key={c.title} delay={(i % 3) * 80} className="h-full">
                    <div className="group h-full rounded-3xl border border-border bg-card p-5 md:p-8 transition-[transform,border-color] duration-200 hover:border-accent/40 hover:-translate-y-1">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/15 border border-primary/25 mb-6 group-hover:bg-accent transition-colors duration-200">
                        <Icon size={22} className="text-accent group-hover:text-accent-foreground transition-colors duration-200" />
                      </div>
                      <h3 className="font-bold text-foreground text-lg md:text-xl mb-3 leading-snug">{c.title}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{c.body}</p>
                    </div>
                  </ScrollReveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ===== GALLERY ===== */}
      {study.screenshots.length > 0 && (
        <section className="py-14 sm:py-20 md:py-28 bg-secondary/30 relative overflow-hidden">
          <div className="container relative">
            <ScrollReveal className="max-w-2xl mb-12">
              <p className="mono-label mb-3">{study.galleryKicker}</p>
              <h2 className="text-[1.55rem] sm:text-3xl md:text-4xl font-bold text-foreground leading-tight">{study.galleryTitle}</h2>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <ScreenshotLightbox images={study.screenshots} layout="grid" />
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* ===== RESULT + STACK ===== */}
      <section className="py-14 sm:py-20 md:py-28 relative overflow-hidden">
        <div className="container relative grid lg:grid-cols-2 gap-10 items-start">
          <ScrollReveal direction="left" distance={60}>
            <p className="mono-label mb-3">{study.resultKicker}</p>
            <h2 className="text-[1.55rem] sm:text-3xl md:text-4xl font-bold text-foreground leading-tight mb-5">{study.resultTitle}</h2>
            <p className="text-muted-foreground leading-relaxed mb-8">{study.resultBody}</p>
            <ul className="space-y-3">
              {study.resultPoints.map((p) => (
                <li key={p} className="flex items-start gap-3 text-foreground">
                  <Check size={18} className="text-accent mt-1 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </ScrollReveal>

          {study.stack.length > 0 && (
            <ScrollReveal direction="right" distance={60} delay={100}>
              <div className="rounded-3xl border border-border bg-card p-5 md:p-10">
                <p className="font-mono text-xs tracking-widest text-accent uppercase mb-5">{study.stackTitle}</p>
                <div className="flex flex-wrap gap-2.5">
                  {study.stack.map((s) => (
                    <span
                      key={s}
                      className="font-mono text-xs tracking-wide px-3 py-1.5 rounded-full border border-border bg-secondary/60 text-muted-foreground"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          )}
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-14 sm:py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="aurora-blob aurora-2 left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[60vw] h-[40vw]" />
        </div>
        <div className="container relative z-10">
          <ScrollReveal className="text-center">
            <h2 className="text-[1.55rem] sm:text-3xl md:text-4xl font-bold text-foreground leading-tight mb-5">{study.ctaTitle}</h2>
            <p className="text-muted-foreground text-[15px] sm:text-lg leading-relaxed mb-9 max-w-xl mx-auto">{study.ctaBody}</p>

            <InlineLeadForm
              id="case-lead-form"
              source={`case:${reference.slug}`}
              sourceLabel={study.client || study.title}
              className="max-w-2xl mx-auto"
            />


            <div className="mt-6 flex justify-center">
              <button
                onClick={goToBlueprint}
                className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-accent transition-colors"
              >
                <Sparkles size={16} className="text-accent" />
                {t("caseStudy.ctaSecondary")}
              </button>
            </div>
          </ScrollReveal>

        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default CaseStudy;
